import React, { ReactNode } from 'react';
import {
    AppBar,
    Box,
    IconButton,
    Toolbar,
    Typography,
    useTheme,
    Avatar,
    Menu,
    MenuItem,
    Tooltip,
    useMediaQuery,
} from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Sidebar from './Sidebar';
import { UserRole } from '../../types/auth';
import { CompanyConfigDialog } from '../company/CompanyConfigDialog';

interface AppLayoutProps {
    children: ReactNode;
}

const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
    const theme = useTheme();
    const { user, logout, updateUser } = useAuth();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [isConfigDialogOpen, setIsConfigDialogOpen] = React.useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const handleOpenCompanyConfig = () => {
        handleMenuClose();
        setIsConfigDialogOpen(true);
    };

    const canConfigureCompany = user?.role === UserRole.RH.toString() || user?.role === UserRole.DIRETOR.toString();

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <AppBar 
                position="fixed" 
                sx={{ 
                    zIndex: theme.zIndex.drawer + 1,
                    backgroundColor: theme.palette.secondary.main,
                    boxShadow: 'none',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                }}
                elevation={0}
            >
                <Toolbar sx={{ justifyContent: 'space-between', minHeight: 64 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {isMobile && (
                            <IconButton
                                aria-label="Abrir menu"
                                edge="start"
                                onClick={() => setMobileMenuOpen(true)}
                                sx={{ color: '#FFFFFF', mr: 1 }}
                            >
                                <MenuIcon />
                            </IconButton>
                        )}
                        <Box
                            component="img"
                            src="/logo.svg"
                            alt="Logo"
                            sx={{
                                height: { xs: 26, sm: 32 },
                                width: 'auto',
                            }}
                        />
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 2 }, ml: 1 }}>
                        <Tooltip title={t('common.profile')}>
                            <IconButton
                                onClick={handleMenuOpen}
                                size="small"
                                sx={{ ml: { xs: 0, sm: 2 } }}
                            >
                                <Avatar
                                    sx={{
                                        bgcolor: theme.palette.primary.main,
                                        width: 32,
                                        height: 32
                                    }}
                                >
                                    {user?.name?.charAt(0).toUpperCase()}
                                </Avatar>
                            </IconButton>
                        </Tooltip>
                    </Box>

                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleMenuClose}
                        onClick={handleMenuClose}
                        PaperProps={{
                            elevation: 0,
                            sx: {
                                overflow: 'visible',
                                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.12))',
                                mt: 1.5,
                                '& .MuiMenuItem-root': {
                                    fontSize: '0.875rem',
                                    py: 1,
                                },
                            },
                        }}
                        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                    >
                        <MenuItem onClick={() => navigate('/profile')}>
                            <Typography variant="body2">{t('menu.profile')}</Typography>
                        </MenuItem>
                        {canConfigureCompany && (
                            <MenuItem onClick={handleOpenCompanyConfig}>
                                <Typography variant="body2">{t('menu.companyConfig')}</Typography>
                            </MenuItem>
                        )}
                        <MenuItem onClick={handleLogout}>
                            <Typography variant="body2" color="error">
                                {t('common.logout')}
                            </Typography>
                        </MenuItem>
                    </Menu>
                </Toolbar>
            </AppBar>

            <Sidebar mobileOpen={mobileMenuOpen} onMobileClose={() => setMobileMenuOpen(false)} />

            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    minWidth: 0, // permite que tabelas largas rolem dentro do conteúdo em vez de estourar a página
                    p: { xs: 1.5, sm: 2, md: 3 },
                    backgroundColor: '#F9FAFB',
                    minHeight: '100vh',
                    pt: { xs: 9, md: 10 },
                }}
            >
                {children}
            </Box>

            {canConfigureCompany && user?.company && (
                <CompanyConfigDialog
                    open={isConfigDialogOpen}
                    onClose={() => setIsConfigDialogOpen(false)}
                    company={user.company}
                    onSave={(updatedCompany) => {
                        // Update the user's company in the context
                        updateUser({
                            ...user,
                            company: updatedCompany
                        });
                        setIsConfigDialogOpen(false);
                    }}
                />
            )}
        </Box>
    );
};

export default AppLayout; 